import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { logError } from '../utils/errorHandler';
import { useCatalog } from './CatalogContext';

const InventoryContext = createContext();

const INVENTORY_STORAGE_KEY = 'inventory';
const DEFAULT_STOCK = 20;
const DEFAULT_LOW_STOCK_THRESHOLD = 5;

const inventoryReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_INVENTORY':
      return {
        ...state,
        stock: action.payload
      };
    case 'SET_STOCK':
      return {
        ...state,
        stock: {
          ...state.stock,
          [action.payload.productId]: action.payload.quantity
        }
      };
    case 'DECREMENT_STOCK': {
      const updates = { ...state.stock };
      action.payload.items.forEach((item) => {
        const current = updates[item.id] ?? 0;
        updates[item.id] = Math.max(current - item.quantity, 0);
      });
      return {
        ...state,
        stock: updates
      };
    }
    default:
      return state;
  }
};

const initialState = {
  stock: {}
};

const buildInitialInventory = (productList) => {
  const initial = {};
  (productList || []).forEach((product) => {
    const fallback = product.inStock ? DEFAULT_STOCK : 0;
    initial[product.id] = typeof product.stock === 'number' ? product.stock : fallback;
  });
  return initial;
};

export const InventoryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(inventoryReducer, initialState);
  const { allProducts } = useCatalog();

  useEffect(() => {
    if (!allProducts || allProducts.length === 0) return;
    try {
      const saved = localStorage.getItem(INVENTORY_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          const defaults = buildInitialInventory(allProducts);
          const merged = { ...defaults };
          Object.keys(defaults).forEach((productId) => {
            const value = parsed[productId];
            if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
              merged[productId] = value;
            }
          });
          const hasAnyStock = Object.values(merged).some((value) => value > 0);
          dispatch({
            type: 'LOAD_INVENTORY',
            payload: hasAnyStock ? merged : defaults
          });
          return;
        }
      }
      dispatch({ type: 'LOAD_INVENTORY', payload: buildInitialInventory(allProducts) });
    } catch (error) {
      logError(error, { action: 'loadInventory' });
      localStorage.removeItem(INVENTORY_STORAGE_KEY);
      dispatch({ type: 'LOAD_INVENTORY', payload: buildInitialInventory(allProducts) });
    }
  }, [allProducts]);

  useEffect(() => {
    try {
      localStorage.setItem(INVENTORY_STORAGE_KEY, JSON.stringify(state.stock));
    } catch (error) {
      logError(error, { action: 'saveInventory' });
    }
  }, [state.stock]);

  const getStock = useCallback((productId) => {
    return state.stock[productId] ?? 0;
  }, [state.stock]);

  const isInStock = useCallback((productId) => {
    return getStock(productId) > 0;
  }, [getStock]);

  const isLowStock = useCallback((productId, threshold = DEFAULT_LOW_STOCK_THRESHOLD) => {
    return getStock(productId) > 0 && getStock(productId) <= threshold;
  }, [getStock]);

  const setStock = useCallback((productId, quantity) => {
    dispatch({ type: 'SET_STOCK', payload: { productId, quantity } });
  }, []);

  const decrementStock = useCallback((items) => {
    dispatch({ type: 'DECREMENT_STOCK', payload: { items } });
  }, []);

  const value = useMemo(() => ({
    getStock,
    isInStock,
    isLowStock,
    setStock,
    decrementStock,
    lowStockThreshold: DEFAULT_LOW_STOCK_THRESHOLD
  }), [getStock, isInStock, isLowStock, setStock, decrementStock]);

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within InventoryProvider');
  }
  return context;
};
